import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
  type FC,
  type ReactNode,
  type JSX,
} from "react";
import type { Message } from "common";
import { useLocation } from "react-router-dom";
import { useConversations } from "@via/hooks/useConversations";
import type { Conversation } from "@via/utils/interfaces";

interface CortexContextProviderProps {
  children?: ReactNode;
}

export interface CortexContextHandler {
  // Context identification
  routePattern: string; // e.g., "/runs/:id", "/projects/:projectId"
  contextName: string; // e.g., "Run Logs", "Project Dashboard"

  // Context data - always a function that returns fresh data
  getContext: () => Record<string, any>; // e.g., { runId, logs, content, filePath, etc. }

  // Optional: Custom UI components
  customContent?: JSX.Element;

  // Optional: System prompt/instructions for AI
  systemPrompt?: string;
}

/** Which body the AI drawer shows. Claude Code is the channel drawer (agent-6-2). */
export type CortexDrawerMode = "insight" | "claude";

interface ICortexContext {
  // Drawer state
  isDrawerOpen: boolean;
  setIsDrawerOpen: (open: boolean) => void;
  drawerMode: CortexDrawerMode;
  setDrawerMode: (mode: CortexDrawerMode) => void;
  selectedTokenId: number | null;
  setSelectedTokenId: (id: number | null) => void;
  shouldUsePortalForCortexDrawer: boolean;
  setShouldUsePortalForCortexDrawer: (value: boolean) => void;

  // Context handler management
  registerContextHandler: (handler: CortexContextHandler) => void;
  unregisterContextHandler: (routePattern: string, contextName: string) => void;
  getCurrentContext: () => CortexContextHandler | null;

  // Conversation management
  conversations: Map<string, Conversation>;
  pagedConversations: Conversation[];
  activeConversationId: string | null;
  activeConversation: Conversation | null;
  openTabIds: Set<string>;
  setActiveConversationId: (id: string | null) => void;
  createNewConversation: () => string;
  updateConversationHistory: (
    id: string,
    history: Array<{ role: "user" | "assistant" | "system"; content: string }>
  ) => void;
  appendUserMessage: (id: string, content: string) => void;
  appendAssistantMessage: (id: string, content: string) => void;
  appendMessage: (id: string, message: Message) => void;
  clearConversationHistory: (id: string) => void;
  updateConversationMeta: (
    id: string,
    payload: Partial<
      Pick<
        Conversation,
        | "conversationHistory"
        | "title"
        | "contextFiles"
        | "isPinned"
        | "pageUrl"
      >
    >
  ) => Promise<void>;
  replaceConversationId: (tempId: string, realId: string) => void;
  deleteConversation: (id: string) => Promise<void>;
  setOpenTabIds: (updater: (prev: Set<string>) => Set<string>) => void;
  conversationListPage: number;
  setConversationListPage: (page: number) => void;
  conversationListPageSize: number;
  setConversationListPageSize: (size: number) => void;
  conversationListSearchKey: string;
  setConversationListSearchKey: (value: string) => void;
  conversationListPinnedOnly: boolean;
  setConversationListPinnedOnly: (value: boolean) => void;
  conversationListTotal: number;
  conversationsLoading: boolean;
  conversationsError: string | null;
}

const initial: ICortexContext = {
  isDrawerOpen: false,
  setIsDrawerOpen: () => {},
  drawerMode: "insight",
  setDrawerMode: () => {},
  selectedTokenId: null,
  setSelectedTokenId: () => {},
  shouldUsePortalForCortexDrawer: false,
  setShouldUsePortalForCortexDrawer: () => {},
  registerContextHandler: () => {},
  unregisterContextHandler: () => {},
  getCurrentContext: () => null,
  conversations: new Map(),
  pagedConversations: [],
  activeConversationId: null,
  activeConversation: null,
  openTabIds: new Set(),
  setActiveConversationId: () => {},
  createNewConversation: () => "",
  updateConversationHistory: () => {},
  appendUserMessage: () => {},
  appendAssistantMessage: () => {},
  appendMessage: () => {},
  clearConversationHistory: () => {},
  updateConversationMeta: async () => {},
  replaceConversationId: () => {},
  deleteConversation: async () => {},
  setOpenTabIds: () => {},
  conversationListPage: 0,
  setConversationListPage: () => {},
  conversationListPageSize: 10,
  setConversationListPageSize: () => {},
  conversationListSearchKey: "",
  setConversationListSearchKey: () => {},
  conversationListPinnedOnly: false,
  setConversationListPinnedOnly: () => {},
  conversationListTotal: 0,
  conversationsLoading: false,
  conversationsError: null,
};

const CortexContext = createContext<ICortexContext>(initial);

// Helper function to match route pattern against pathname
function matchRoute(pattern: string, pathname: string): boolean {
  // Convert route pattern to regex
  // e.g., "/runs/:id" -> "^/runs/([^/]+)$"
  const regexPattern = "^" + pattern.replace(/:[^/]+/g, "([^/]+)") + "$";
  const regex = new RegExp(regexPattern);
  return regex.test(pathname);
}

// Create the cortex context provider
const CortexProvider: FC<CortexContextProviderProps> = ({ children }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(
    initial.isDrawerOpen
  );
  const [drawerMode, setDrawerMode] = useState<CortexDrawerMode>(
    initial.drawerMode
  );
  const [selectedTokenId, setSelectedTokenId] = useState<number | null>(
    initial.selectedTokenId
  );
  const [shouldUsePortalForCortexDrawer, setShouldUsePortalForCortexDrawer] =
    useState<boolean>(initial.shouldUsePortalForCortexDrawer);
  const [contextHandlers, setContextHandlers] = useState<
    Map<string, CortexContextHandler>
  >(new Map());
  const [openTabIds, setOpenTabIds] = useState<Set<string>>(new Set());
  const { pathname } = useLocation();

  // Conversation list pagination & search state
  const [conversationListPage, setConversationListPage] = useState<number>(0);
  const [conversationListPageSize, setConversationListPageSize] =
    useState<number>(10);
  const [conversationListSearchKey, setConversationListSearchKey] =
    useState<string>("");
  const [conversationListPinnedOnly, setConversationListPinnedOnly] =
    useState<boolean>(false);

  // Get current context to determine conversation context
  const getCurrentContext = useCallback((): CortexContextHandler | null => {
    // Try to find matching handlers for current route.
    // If multiple handlers match, prefer the most recently registered one.
    const handlers = Array.from(contextHandlers.values());
    for (let i = handlers.length - 1; i >= 0; i--) {
      const handler = handlers[i];
      if (matchRoute(handler.routePattern, pathname)) return handler;
    }
    return null;
  }, [contextHandlers, pathname]);

  const {
    conversations,
    pagedConversations,
    activeConversationId,
    activeConversation,
    setActiveConversationId,
    createNewConversation,
    updateConversationHistory,
    appendUserMessage,
    appendAssistantMessage,
    appendMessage,
    clearConversationHistory,
    updateConversationMeta,
    replaceConversationId,
    deleteConversation,
    total: conversationListTotal,
    isLoading: conversationsLoading,
    error: conversationsError,
  } = useConversations({
    enabled: isDrawerOpen, // Only load when drawer is open
    page: conversationListPage,
    pageSize: conversationListPageSize,
    searchKey: conversationListSearchKey,
    pinnedOnly: conversationListPinnedOnly,
  });

  // Generate unique key for handler
  const getHandlerKey = useCallback(
    (routePattern: string, contextName: string) => {
      return `${routePattern}::${contextName}`;
    },
    []
  );

  // Register a context handler (uses routePattern + contextName as unique key)
  const registerContextHandler = useCallback(
    (handler: CortexContextHandler) => {
      setContextHandlers(prev => {
        const newMap = new Map(prev);
        const key = getHandlerKey(handler.routePattern, handler.contextName);
        newMap.set(key, handler);
        return newMap;
      });
    },
    [getHandlerKey]
  );

  // Unregister a context handler
  const unregisterContextHandler = useCallback(
    (routePattern: string, contextName: string) => {
      setContextHandlers(prev => {
        const newMap = new Map(prev);
        const key = getHandlerKey(routePattern, contextName);
        newMap.delete(key);
        return newMap;
      });
    },
    [getHandlerKey]
  );

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      isDrawerOpen,
      setIsDrawerOpen,
      drawerMode,
      setDrawerMode,
      selectedTokenId,
      setSelectedTokenId,
      shouldUsePortalForCortexDrawer,
      setShouldUsePortalForCortexDrawer,
      registerContextHandler,
      unregisterContextHandler,
      getCurrentContext,
      conversations,
      pagedConversations,
      activeConversationId,
      activeConversation,
      openTabIds,
      setActiveConversationId,
      createNewConversation,
      updateConversationHistory,
      appendUserMessage,
      appendAssistantMessage,
      appendMessage,
      clearConversationHistory,
      updateConversationMeta,
      replaceConversationId,
      deleteConversation,
      setOpenTabIds,
      conversationListPage,
      setConversationListPage,
      conversationListPageSize,
      setConversationListPageSize,
      conversationListSearchKey,
      setConversationListSearchKey,
      conversationListPinnedOnly,
      setConversationListPinnedOnly,
      conversationListTotal,
      conversationsLoading,
      conversationsError,
    }),
    [
      isDrawerOpen,
      drawerMode,
      selectedTokenId,
      shouldUsePortalForCortexDrawer,
      registerContextHandler,
      unregisterContextHandler,
      getCurrentContext,
      conversations,
      pagedConversations,
      activeConversationId,
      activeConversation,
      openTabIds,
      setActiveConversationId,
      createNewConversation,
      updateConversationHistory,
      appendUserMessage,
      appendAssistantMessage,
      appendMessage,
      clearConversationHistory,
      replaceConversationId,
      deleteConversation,
      setOpenTabIds,
      conversationListPage,
      conversationListPageSize,
      conversationListSearchKey,
      conversationListPinnedOnly,
      conversationListTotal,
      conversationsLoading,
      conversationsError,
    ]
  );

  return (
    <CortexContext.Provider value={contextValue}>
      {children}
    </CortexContext.Provider>
  );
};

// Custom hook to use cortex context
function useCortexContext(): ICortexContext {
  return useContext(CortexContext);
}

export { CortexContext, CortexProvider, useCortexContext, type ICortexContext };
